import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, OnInit, inject, signal, viewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import confetti from 'canvas-confetti';

import { RealTimeService } from '@shared/services/real-time/real-time.service';
import { CompanyContextService } from '@pages/empresas/services/company-context.service';
import { CelebrationsService } from '../../services/celebrations.service';
import { CelebrationPayload } from '../../models/celebration.model';
import { playCelebrationAnimation } from '../../celebration-animations';
import { elegirCelebracion } from '../../celebration-coalescer';

/**
 * M25.1 — Celebraciones del Éxito. Montado una sola vez en `AppLayoutComponent` (junto a
 * `app-modal-host`), igual de global: escucha el evento SSE `celebration.trigger` que ya emite
 * el backend vía el mismo canal que `task.statusChanged`/`board.presence` (RT-001, sin tráfico
 * de red dedicado), y dibuja la animación en un `<canvas>` propio superpuesto a toda la pantalla.
 *
 * RN-002: la preferencia individual del usuario tiene prioridad absoluta — si está desactivada,
 * este componente ni siquiera muestra el mensaje estático, no solo omite el confeti.
 * RNF-005: si el sistema operativo pide `prefers-reduced-motion`, se sustituye la animación por
 * un aviso estático (WCAG 2.1 AA).
 */
@Component({
  selector: 'app-celebration-overlay',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <canvas #canvasRef class="pointer-events-none fixed inset-0 z-[999999]" style="width:100vw;height:100vh;"></canvas>
    @if (toast(); as t) {
      <div role="status" aria-live="polite"
           class="pointer-events-none fixed top-6 left-1/2 -translate-x-1/2 z-[999999]
                  flex items-center gap-2.5 rounded-full bg-gray-900/95 dark:bg-gray-800/95 text-white
                  px-5 py-3 shadow-2xl text-sm font-bold animate-[celebration-pop_.3s_ease-out]">
        <i class="bi bi-trophy-fill text-amber-400 text-lg" aria-hidden="true"></i>
        <span>{{ t }}</span>
      </div>
    }
  `,
  styles: [`
    @keyframes celebration-pop {
      0% { opacity: 0; transform: translate(-50%, -12px) scale(.9); }
      100% { opacity: 1; transform: translate(-50%, 0) scale(1); }
    }
  `],
})
export class CelebrationOverlayComponent implements OnInit {
  private readonly realTime = inject(RealTimeService);
  private readonly companyContext = inject(CompanyContextService);
  private readonly celebrationsService = inject(CelebrationsService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly canvasRef = viewChild.required<ElementRef<HTMLCanvasElement>>('canvasRef');

  readonly toast = signal<string | null>(null);
  private toastTimer: ReturnType<typeof setTimeout> | null = null;

  private preferenceEnabled = true;
  private fire: confetti.CreateTypes | null = null;

  /**
   * Ventana de coalescing (ms): las celebraciones que lleguen juntas —un mismo cierre produce
   * proyecto + hito + tarea— se agrupan y se muestra la de mayor prioridad en vez de apilarse.
   * 400 ms mantiene la primera visible casi al instante (RT-001: sensación de tiempo real).
   */
  private static readonly VENTANA_MS = 400;
  private pendientes: CelebrationPayload[] = [];
  private ventanaTimer: ReturnType<typeof setTimeout> | null = null;

  ngOnInit(): void {
    this.loadPreference();

    const sub = this.realTime.on<CelebrationPayload>('celebration.trigger').subscribe(payload => {
      this.handleCelebration(payload);
    });
    this.destroyRef.onDestroy(() => sub.unsubscribe());
  }

  private loadPreference(): void {
    const companyId = this.companyContext.companyId();
    if (!companyId) return;
    this.celebrationsService.myPreference(companyId).subscribe({
      next: res => { this.preferenceEnabled = res.celebrationsEnabled; },
      error: () => { /* si falla la lectura, se asume habilitado (default del backend) */ },
    });
  }

  private handleCelebration(payload: CelebrationPayload): void {
    // RN-002: la preferencia individual manda — ni el mensaje estático se muestra si está apagada.
    if (!this.preferenceEnabled) return;

    // Coalescing: se agrupan en una ventana corta y se muestra la de mayor prioridad. Un
    // project_completed repetido (mismo tipo + título) se deduplica dentro de la ventana.
    this.pendientes.push(payload);
    if (this.ventanaTimer !== null) return;
    this.ventanaTimer = setTimeout(() => {
      this.ventanaTimer = null;
      const candidatas = this.pendientes;
      this.pendientes = [];
      const elegida = elegirCelebracion(candidatas);
      if (elegida === null) return;

      const reducedMotion = typeof matchMedia !== 'undefined'
        && matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.showToast(elegida.title);
      if (!reducedMotion) {
        this.playAnimation(elegida.animationType);
      }
    }, CelebrationOverlayComponent.VENTANA_MS);
  }

  private showToast(title: string): void {
    if (this.toastTimer) clearTimeout(this.toastTimer);
    this.toast.set(`¡${title}! 🎉`);
    this.toastTimer = setTimeout(() => this.toast.set(null), 4000);
  }

  private playAnimation(type: CelebrationPayload['animationType']): void {
    const fire = this.ensureConfettiInstance();
    if (!fire) return;
    playCelebrationAnimation(fire, type);
  }

  private ensureConfettiInstance(): confetti.CreateTypes | null {
    if (this.fire) return this.fire;
    const canvas = this.canvasRef().nativeElement;
    this.fire = confetti.create(canvas, { resize: true, useWorker: true });
    return this.fire;
  }
}
